import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import './AstroToolsPage.css'
import PageLayout from '../components/layout/PageLayout'
import Reveal from '../components/common/Reveal'
import { DateField, TimeField } from '@astro/date-field'
import { KundliStudio, LocationSelect, ksSelectStyles } from '@astro/kundli-studio'
import { useToast } from '../context/ToastContext'
import { getKundli, getPanchang, getMatchReport, getPlanetTransits, buildTransitReading, resolveLocation, buildTime } from '@astro/vedastro'
import { appUrl } from '@astro/config'

/* ─────────────────────────── Static data ─────────────────────────── */
const TOOLS = [
  { key: 'kundli', icon: '📜', title: 'Free Kundli', tag: 'Birth Chart', desc: 'Generate your Vedic birth chart with Rashi, Nakshatra and Lagna in seconds.' },
  { key: 'matching', icon: '💞', title: 'Kundli Matching', tag: 'Guna Milan', desc: 'Check marriage compatibility with the classic 36-point Ashtakoota system.' },
  { key: 'panchang', icon: '📅', title: 'Panchang', tag: 'Hindu Almanac', desc: 'See the Tithi, Nakshatra, Yoga, Karana and sunrise for any day.' },
  { key: 'lucky', icon: '🍀', title: 'Daily Lucky Picks', tag: 'Fortune', desc: 'Your lucky number, colour, gemstone and direction for today.' },
  { key: 'numerology', icon: '🔢', title: 'Birth Number', tag: 'Numerology', desc: 'Discover your Moolank & Bhagyank and the planet that rules your life path.' },
]

const RASHIS = [
  { en: 'Aries', sa: 'Mesha', lord: 'Mars', glyph: '♈' }, { en: 'Taurus', sa: 'Vrishabha', lord: 'Venus', glyph: '♉' },
  { en: 'Gemini', sa: 'Mithuna', lord: 'Mercury', glyph: '♊' }, { en: 'Cancer', sa: 'Karka', lord: 'Moon', glyph: '♋' },
  { en: 'Leo', sa: 'Simha', lord: 'Sun', glyph: '♌' }, { en: 'Virgo', sa: 'Kanya', lord: 'Mercury', glyph: '♍' },
  { en: 'Libra', sa: 'Tula', lord: 'Venus', glyph: '♎' }, { en: 'Scorpio', sa: 'Vrischika', lord: 'Mars', glyph: '♏' },
  { en: 'Sagittarius', sa: 'Dhanu', lord: 'Jupiter', glyph: '♐' }, { en: 'Capricorn', sa: 'Makara', lord: 'Saturn', glyph: '♑' },
  { en: 'Aquarius', sa: 'Kumbha', lord: 'Saturn', glyph: '♒' }, { en: 'Pisces', sa: 'Meena', lord: 'Jupiter', glyph: '♓' },
]

const WEEKDAYS = [
  { en: 'Sunday', sa: 'Ravivar', lord: 'Sun' }, { en: 'Monday', sa: 'Somvar', lord: 'Moon' },
  { en: 'Tuesday', sa: 'Mangalvar', lord: 'Mars' }, { en: 'Wednesday', sa: 'Budhvar', lord: 'Mercury' },
  { en: 'Thursday', sa: 'Guruvar', lord: 'Jupiter' }, { en: 'Friday', sa: 'Shukravar', lord: 'Venus' },
  { en: 'Saturday', sa: 'Shanivar', lord: 'Saturn' },
]

const LUCKY_COLORS = ['Saffron', 'Deep Red', 'Golden Yellow', 'Emerald Green', 'Royal Blue', 'Ivory White', 'Amber', 'Maroon', 'Turquoise', 'Marigold', 'Sandalwood', 'Coral']
const GEMSTONES = ['Ruby', 'Pearl', 'Red Coral', 'Emerald', 'Yellow Sapphire', 'Diamond', 'Blue Sapphire', 'Hessonite', "Cat's Eye"]
const DIRECTIONS = ['East', 'North-East', 'North', 'North-West', 'West', 'South-West', 'South', 'South-East']
const METALS = ['Gold', 'Silver', 'Copper', 'Bronze', 'Panchdhatu']
const MANTRAS = ['Om Suryaya Namah', 'Om Chandraya Namah', 'Om Mangalaya Namah', 'Om Budhaya Namah', 'Om Gurave Namah', 'Om Shukraya Namah', 'Om Shanaye Namah']

// Vedic / Chaldean numerology: number → ruling planet & traits
const NUMBER_PLANETS = {
  1: { planet: 'Sun', traits: 'Leadership, independence, ambition and a pioneering spirit.', day: 'Sunday', colour: 'Golden Orange' },
  2: { planet: 'Moon', traits: 'Sensitivity, intuition, diplomacy and emotional depth.', day: 'Monday', colour: 'White / Cream' },
  3: { planet: 'Jupiter', traits: 'Wisdom, optimism, creativity and a love of learning.', day: 'Thursday', colour: 'Yellow' },
  4: { planet: 'Rahu', traits: 'Discipline, originality, practicality and quiet resilience.', day: 'Sunday', colour: 'Grey / Blue' },
  5: { planet: 'Mercury', traits: 'Communication, adaptability, wit and restless energy.', day: 'Wednesday', colour: 'Green' },
  6: { planet: 'Venus', traits: 'Charm, harmony, artistry and a magnetic warmth.', day: 'Friday', colour: 'Rose / White' },
  7: { planet: 'Ketu', traits: 'Spirituality, depth, imagination and a philosophical mind.', day: 'Monday', colour: 'Sea Green' },
  8: { planet: 'Saturn', traits: 'Endurance, responsibility, ambition and material mastery.', day: 'Saturday', colour: 'Dark Blue / Black' },
  9: { planet: 'Mars', traits: 'Courage, drive, passion and a protective nature.', day: 'Tuesday', colour: 'Red' },
}

const GUNA_KOOTAS = [
  { name: 'Varna', max: 1, note: 'Spiritual compatibility & ego balance' },
  { name: 'Vashya', max: 2, note: 'Mutual attraction & control' },
  { name: 'Tara', max: 3, note: 'Health & wellbeing of the union' },
  { name: 'Yoni', max: 4, note: 'Physical & intimate compatibility' },
  { name: 'Graha Maitri', max: 5, note: 'Mental affinity & friendship' },
  { name: 'Gana', max: 6, note: 'Temperament & nature' },
  { name: 'Bhakoot', max: 7, note: 'Love, finances & family growth' },
  { name: 'Nadi', max: 8, note: 'Health, genes & progeny' },
]

/* ─────────────────────────── Helpers ─────────────────────────── */
function hash(str) {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}
const pick = (arr, seed) => arr[((Math.trunc(seed) % arr.length) + arr.length) % arr.length]

// Reduce a number to a single digit (1-9)
function digitRoot(n) {
  let x = Math.abs(n)
  while (x > 9) x = String(x).split('').reduce((a, c) => a + Number(c), 0)
  return x || 0
}

function parseDate(str) {
  if (!str) return null
  const d = new Date(`${str}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Date → 'YYYY-MM-DD', the string shape every tool here already sends. */
/** Today as 'YYYY-MM-DD', for capping date-of-birth fields. */
function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}


function fmtLongDate(date) {
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

/* ─────────────────────────── Reusable UI ─────────────────────────── */
function ResultRow({ label, value, accent }) {
  return (
    <div className="at-row">
      <span className="at-row-label">{label}</span>
      <span className={`at-row-value${accent ? ' at-row-accent' : ''}`}>{value}</span>
    </div>
  )
}

function EmptyState({ icon, text }) {
  return (
    <div className="at-empty">
      <div className="at-empty-icon">{icon}</div>
      <p>{text}</p>
    </div>
  )
}

// Shimmer block helper — a rounded skeleton bar. `bg` switches light/dark panels.
function SkBar({ w, h = 'h-3', bg = 'bg-stone-200/80', className = '' }) {
  return <div className={`${h} rounded-full ${bg} ${className}`} style={w ? { width: w } : undefined} />
}

// Skeleton for the dark "Today's Horoscope" widget body.
function HoroscopeWidgetSkeleton() {
  return (
    <div className="animate-pulse" aria-hidden="true">
      <div className="space-y-3">
        <SkBar w="100%" bg="bg-white/10" />
        <SkBar w="94%" bg="bg-white/10" />
        <SkBar w="98%" bg="bg-white/10" />
        <SkBar w="62%" bg="bg-white/10" />
      </div>
      <SkBar w="150px" h="h-2.5" bg="bg-white/10" className="mt-5" />
      <div className="horo-lucky">
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <SkBar w="48px" h="h-2" bg="bg-white/10" className="mx-auto" />
            <SkBar w="34px" h="h-4" bg="bg-white/10" className="mx-auto mt-2.5" />
          </div>
        ))}
      </div>
      <SkBar w="180px" h="h-3" bg="bg-white/10" className="mt-6" />
    </div>
  )
}

// Skeleton mirroring a result panel of labelled rows (Kundli & Panchang).
function RowsSkeleton({ rows = 6 }) {
  return (
    <div className="animate-pulse" aria-hidden="true">
      <div className="at-result-head">
        <span className="at-result-glyph" style={{ background: '#ece5da', borderColor: 'rgba(245,158,11,.15)' }} />
        <div className="flex-1">
          <SkBar w="150px" h="h-4" />
          <SkBar w="110px" h="h-2.5" className="mt-2 bg-stone-200/60" />
        </div>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="at-row" key={i}>
          <SkBar w={`${96 + (i % 3) * 22}px`} className="bg-stone-200/70" />
          <SkBar w={`${72 + (i % 2) * 34}px`} h="h-3.5" />
        </div>
      ))}
    </div>
  )
}

// Skeleton mirroring the Panchang left-column hero + sunrise/sunset cards.
function PanchangHeroSkeleton() {
  return (
    <div className="animate-pulse" aria-hidden="true">
      <div className="at-panchang-hero" style={{ alignItems: 'center' }}>
        <SkBar w="140px" h="h-6" className="bg-stone-200/70" />
        <SkBar w="180px" h="h-2.5" className="mt-1 bg-stone-200/60" />
        <SkBar w="120px" h="h-2.5" className="mt-1 bg-stone-200/50" />
      </div>
      <div className="at-suntimes" style={{ marginTop: '12px' }}>
        {[0, 1].map((i) => (
          <div key={i}>
            <SkBar w="26px" h="h-6" className="mx-auto bg-stone-200/70" />
            <SkBar w="52px" h="h-2.5" className="mx-auto mt-2 bg-stone-200/60" />
            <SkBar w="70px" h="h-4" className="mx-auto mt-2" />
          </div>
        ))}
      </div>
    </div>
  )
}

// Skeleton mirroring the Matching score ring + koota rows.
function MatchSkeleton({ rows = 6 }) {
  return (
    <div className="animate-pulse" aria-hidden="true">
      <div className="at-score">
        <div className="at-score-ring" style={{ background: '#e3dbcf', boxShadow: 'none' }} />
        <div className="flex-1">
          <SkBar w="170px" h="h-4" />
          <SkBar w="120px" h="h-2.5" className="mt-2 bg-stone-200/60" />
        </div>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="at-koota" key={i}>
          <div className="at-koota-head">
            <SkBar w="90px" />
            <SkBar w="30px" />
          </div>
          <div className="at-koota-bar" />
          <SkBar w={`${150 + (i % 2) * 30}px`} h="h-2.5" className="mt-1.5 bg-stone-200/50" />
        </div>
      ))}
    </div>
  )
}

function ErrorState({ text }) {
  return (
    <div className="at-empty">
      <div className="at-empty-icon">🔭</div>
      <p>{text}</p>
    </div>
  )
}

// Map a VedAstro English sign name (e.g. "Libra") to its Sanskrit name & lord.
function signInfo(en) {
  return RASHIS.find((r) => r.en === en) || { en: en || '—', sa: en || '—', lord: '—' }
}
// Map a VedAstro weekday name (e.g. "Sunday") to Sanskrit name & lord.
function weekdayInfo(en) {
  return WEEKDAYS.find((w) => w.en === en) || { en: en || '—', sa: en || '—', lord: '—' }
}

/* ─────────────────────────── Tool: Kundli ─────────────────────────── */
const GENDER_OPTIONS = ['Male', 'Female', 'Other'].map((g) => ({ value: g, label: g }))

function KundliTool({ showToast }) {
  const [form, setForm] = useState({ name: '', gender: 'Male', dob: '', tob: '', pob: '', location: null })
  const [status, setStatus] = useState('idle') // idle | loading | error | done
  const [result, setResult] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const generate = async (e) => {
    e.preventDefault()
    const date = parseDate(form.dob)
    if (!date) return showToast('Please enter a valid date of birth.')

    const my = ++reqRef.current
    setStatus('loading')
    try {
      const location = form.location || (await resolveLocation(form.pob))
      const data = await getKundli({ dob: form.dob, tob: form.tob, location }, undefined)
      if (my !== reqRef.current) return
      const moon = signInfo(data.moonSign)
      setResult({
        name: form.name.trim() || 'Seeker',
        sun: signInfo(data.sunSign).en,
        moon,
        lagna: signInfo(data.lagna),
        nakshatra: data.nakshatra,
        pada: data.pada || '1',
        birthNumber: digitRoot(date.getDate()),
        when: fmtLongDate(date),
      })
      setStatus('done')
      showToast('Your Kundli has been cast. 🔮')
    } catch (err) {
      if (my !== reqRef.current) return
      setErrorMsg(err?.message || 'We could not cast your Kundli right now. Please try again.')
      setStatus('error')
    }
  }

  return (
    <div className="at-tool-grid">
      <form className="at-form" onSubmit={generate}>
        <div>
          <label className="flbl" htmlFor="k-name">Full Name</label>
          <input id="k-name" name="name" value={form.name} onChange={onChange} className="field" placeholder="Your name" />
        </div>
        <div className="at-form-2">
          <div>
            <label className="flbl" htmlFor="k-gender">Gender</label>
            <Select
              inputId="k-gender"
              options={GENDER_OPTIONS}
              value={GENDER_OPTIONS.find((o) => o.value === form.gender)}
              onChange={(o) => setForm((f) => ({ ...f, gender: o?.value || 'Male' }))}
              styles={ksSelectStyles}
              isSearchable={false}
            />
          </div>
          <div>
            <label className="flbl" htmlFor="k-dob">Date of Birth *</label>
            <DateField id="k-dob" name="dob" value={form.dob} onChange={onChange} max={todayISO()} required />
          </div>
        </div>
        <div className="at-form-2">
          <div>
            <label className="flbl" htmlFor="k-tob">Time of Birth</label>
            <TimeField id="k-tob" name="tob" value={form.tob} onChange={onChange} />
          </div>
          <div>
            <label className="flbl" htmlFor="k-pob">Place of Birth</label>
            <LocationSelect
              inputId="k-pob"
              value={form.location}
              onChange={(loc) => setForm((f) => ({ ...f, location: loc, pob: loc?.Name || '' }))}
              placeholder="Search your birth city…"
            />
          </div>
        </div>
        <button type="submit" className="at-submit shine-btn" disabled={status === 'loading'}>
          {status === 'loading' ? 'Casting your Kundli…' : 'Generate Kundli ✦'}
        </button>
      </form>

      <div className="at-result">
        {status === 'loading' && <RowsSkeleton rows={6} />}
        {status === 'error' && <ErrorState text={errorMsg} />}
        {status === 'done' && result && (
          <>
            <div className="at-result-head">
              <span className="at-result-glyph">🕉️</span>
              <div>
                <h3>{result.name}&apos;s Kundli</h3>
                <p>{result.when}</p>
              </div>
            </div>
            <ResultRow label="Sun Sign (Surya Rashi)" value={result.sun} accent />
            <ResultRow label="Moon Sign (Chandra Rashi)" value={`${result.moon.sa} · ${result.moon.en}`} accent />
            <ResultRow label="Ascendant (Lagna)" value={`${result.lagna.sa} · ${result.lagna.en}`} />
            <ResultRow label="Rashi Lord" value={result.moon.lord} />
            <ResultRow label="Nakshatra" value={`${result.nakshatra} · Pada ${result.pada}`} />
            <ResultRow label="Birth Number (Moolank)" value={result.birthNumber} />
            <p className="at-note">
              For a full detailed chart with planetary positions, dashas and remedies, book a personal reading with Maharaj Ji.
            </p>
          </>
        )}
        {status === 'idle' && (
          <EmptyState icon="📜" text="Fill in your birth details to cast your Vedic Kundli." />
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Tool: Matching ─────────────────────────── */
// Which VedAstro MatchReport factor(s) back each classic Ashtakoota kuta.
const KUTA_FACTOR_KEYS = {
  Varna: ['varna'],
  Vashya: ['vasya', 'vashya'],
  Tara: ['dina', 'tara'],
  Yoni: ['yoni'],
  'Graha Maitri': ['graha maitram', 'graha'],
  Gana: ['gana', 'guna kuta'],
  Bhakoot: ['rasi', 'bhakoot'],
  Nadi: ['nadi'],
}

// Convert a factor's Good/Bad/Neutral nature into points out of its max.
function natureToPoints(nature, max) {
  if (nature === 'Good') return max
  if (nature === 'Bad') return 0
  return Math.round(max / 2) // Neutral / Empty / unknown
}

function MatchingTool({ showToast }) {
  const [form, setForm] = useState({
    boy: '', boyDob: '', boyTob: '', boyPob: '', boyLocation: null,
    girl: '', girlDob: '', girlTob: '', girlPob: '', girlLocation: null,
  })
  const [status, setStatus] = useState('idle') // idle | loading | error | done
  const [result, setResult] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const match = async (e) => {
    e.preventDefault()
    if (!parseDate(form.boyDob) || !parseDate(form.girlDob)) return showToast('Please enter both dates of birth.')

    const my = ++reqRef.current
    setStatus('loading')
    try {
      const [boyLocation, girlLocation] = await Promise.all([
        form.boyLocation || resolveLocation(form.boyPob),
        form.girlLocation || resolveLocation(form.girlPob),
      ])
      if (my !== reqRef.current) return
      const maleTime = buildTime(form.boyDob, form.boyTob, boyLocation)
      const femaleTime = buildTime(form.girlDob, form.girlTob, girlLocation)
      const { factors } = await getMatchReport({ maleTime, femaleTime }, undefined)
      if (my !== reqRef.current) return

      const findNature = (keys) => {
        const f = factors.find((x) => keys.some((k) => (x.Name || '').toLowerCase().includes(k)))
        return f ? f.Nature : 'Neutral'
      }
      let total = 0
      const rows = GUNA_KOOTAS.map((k) => {
        const nature = findNature(KUTA_FACTOR_KEYS[k.name] || [])
        const got = natureToPoints(nature, k.max)
        total += got
        return { ...k, got }
      })
      const verdict =
        total >= 28 ? { label: 'Excellent Match', tone: 'good' } :
          total >= 18 ? { label: 'Good Match', tone: 'ok' } :
            { label: 'Needs Consideration', tone: 'low' }
      setResult({ rows, total, verdict })
      setStatus('done')
      showToast(`Guna Milan complete — ${total}/36 points.`)
    } catch (err) {
      if (my !== reqRef.current) return
      setErrorMsg(err?.message || 'We could not compute the match right now. Please try again.')
      setStatus('error')
    }
  }

  return (
    <div className="at-tool-grid">
      <form className="at-form" onSubmit={match}>
        <p className="at-form-legend">Groom&apos;s Details</p>
        <div className="at-form-2">
          <div>
            <label className="flbl" htmlFor="m-boy">Name</label>
            <input id="m-boy" name="boy" value={form.boy} onChange={onChange} className="field" placeholder="Groom" />
          </div>
          <div>
            <label className="flbl" htmlFor="m-boy-dob">Date of Birth *</label>
            <DateField id="m-boy-dob" name="boyDob" value={form.boyDob} onChange={onChange} max={todayISO()} required />
          </div>
        </div>
        <div className="at-form-2">
          <div>
            <label className="flbl" htmlFor="m-boy-tob">Time of Birth</label>
            <TimeField id="m-boy-tob" name="boyTob" value={form.boyTob} onChange={onChange} />
          </div>
          <div>
            <label className="flbl" htmlFor="m-boy-pob">Place of Birth</label>
            <LocationSelect
              inputId="m-boy-pob"
              value={form.boyLocation}
              onChange={(loc) => setForm((f) => ({ ...f, boyLocation: loc, boyPob: loc?.Name || '' }))}
              placeholder="Search birth city…"
            />
          </div>
        </div>
        <p className="at-form-legend">Bride&apos;s Details</p>
        <div className="at-form-2">
          <div>
            <label className="flbl" htmlFor="m-girl">Name</label>
            <input id="m-girl" name="girl" value={form.girl} onChange={onChange} className="field" placeholder="Bride" />
          </div>
          <div>
            <label className="flbl" htmlFor="m-girl-dob">Date of Birth *</label>
            <DateField id="m-girl-dob" name="girlDob" value={form.girlDob} onChange={onChange} max={todayISO()} required />
          </div>
        </div>
        <div className="at-form-2">
          <div>
            <label className="flbl" htmlFor="m-girl-tob">Time of Birth</label>
            <TimeField id="m-girl-tob" name="girlTob" value={form.girlTob} onChange={onChange} />
          </div>
          <div>
            <label className="flbl" htmlFor="m-girl-pob">Place of Birth</label>
            <LocationSelect
              inputId="m-girl-pob"
              value={form.girlLocation}
              onChange={(loc) => setForm((f) => ({ ...f, girlLocation: loc, girlPob: loc?.Name || '' }))}
              placeholder="Search birth city…"
            />
          </div>
        </div>
        <button type="submit" className="at-submit shine-btn" disabled={status === 'loading'}>
          {status === 'loading' ? 'Consulting the charts…' : 'Check Compatibility ✦'}
        </button>
      </form>

      <div className="at-result">
        {status === 'loading' && <MatchSkeleton rows={6} />}
        {status === 'error' && <ErrorState text={errorMsg} />}
        {status === 'done' && result && (
          <>
            <div className="at-score">
              <div className={`at-score-ring at-score-${result.verdict.tone}`}>
                <span className="at-score-num">{result.total}</span>
                <span className="at-score-max">/ 36</span>
              </div>
              <div>
                <h3>{result.verdict.label}</h3>
                <p>Ashtakoota Guna Milan</p>
              </div>
            </div>
            {result.rows.map((r) => (
              <div className="at-koota" key={r.name}>
                <div className="at-koota-head">
                  <span className="at-koota-name">{r.name}</span>
                  <span className="at-koota-score">{r.got}/{r.max}</span>
                </div>
                <div className="at-koota-bar">
                  <div className="at-koota-fill" style={{ width: `${(r.got / r.max) * 100}%` }} />
                </div>
                <p className="at-koota-note">{r.note}</p>
              </div>
            ))}
            <p className="at-note">
              This is an indicative Guna score. A complete match also weighs Mangal Dosha and planetary strengths — ask Maharaj Ji for a full analysis.
            </p>
          </>
        )}
        {status === 'idle' && (
          <EmptyState icon="💞" text="Enter both partners' details to compute the 36-point Guna Milan." />
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Tool: Panchang ─────────────────────────── */
function PanchangTool() {
  const [dateStr, setDateStr] = useState(() => new Date().toISOString().slice(0, 10))
  const [status, setStatus] = useState('loading') // loading | error | done
  const [data, setData] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)

  useEffect(() => {
    // Clearing the field leaves an empty string — don't fetch a blank date.
    if (!dateStr) return undefined
    const my = ++reqRef.current
    const controller = new AbortController()
    setStatus('loading')
    getPanchang({ date: dateStr }, controller.signal)
      .then((p) => {
        if (my !== reqRef.current) return
        const date = parseDate(dateStr) || new Date()
        const [nakName, pada] = String(p.nakshatra).split(' - ')
        setData({
          when: fmtLongDate(date),
          weekday: weekdayInfo(p.vara),
          tithi: p.tithi,
          nakshatra: pada ? `${nakName.trim()} · Pada ${pada.trim()}` : nakName,
          yoga: p.yoga?.Name || p.yoga,
          karana: p.karana,
          sunrise: p.sunrise,
          sunset: p.sunset,
          moonSign: signInfo(p.moonSign),
        })
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not load the Panchang right now. Please try again.')
        setStatus('error')
      })
    return () => controller.abort()
  }, [dateStr])

  const loadingFirst = status === 'loading' && !data
  const errorFirst = status === 'error' && !data

  return (
    <div className="at-tool-grid">
      <div className="at-form">
        <div>
          <label className="flbl" htmlFor="p-date">Select Date</label>
          <DateField id="p-date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
        </div>
        {data && (
          <>
            <div className="at-panchang-hero">
              <span className="at-panchang-day">{data.weekday.sa}</span>
              <span className="at-panchang-sub">{data.weekday.en} · Ruled by {data.weekday.lord}</span>
              <span className="at-panchang-date">{data.when}</span>
            </div>
            <div className="at-suntimes">
              <div><span className="at-sun-ico">🌅</span><span className="flbl">Sunrise</span><strong>{data.sunrise}</strong></div>
              <div><span className="at-sun-ico">🌇</span><span className="flbl">Sunset</span><strong>{data.sunset}</strong></div>
            </div>
          </>
        )}
        {loadingFirst && <PanchangHeroSkeleton />}
      </div>

      <div className="at-result">
        {loadingFirst && <RowsSkeleton rows={6} />}
        {errorFirst && <ErrorState text={errorMsg} />}
        {data && (
          <>
            <div className="at-result-head">
              <span className="at-result-glyph">📅</span>
              <div>
                <h3>Today&apos;s Panchang</h3>
                <p>The five limbs of the Vedic almanac</p>
              </div>
            </div>
            <ResultRow label="Tithi" value={`${data.tithi.Name} (${data.tithi.Paksha} Paksha)`} accent />
            <ResultRow label="Nakshatra" value={data.nakshatra} accent />
            <ResultRow label="Yoga" value={data.yoga} />
            <ResultRow label="Karana" value={data.karana} />
            <ResultRow label="Vaar (Weekday)" value={`${data.weekday.sa} · ${data.weekday.en}`} />
            <ResultRow label="Moon Rashi" value={`${data.moonSign.sa} · ${data.moonSign.en}`} />
            <p className="at-note">
              Panchang values are computed for New Delhi (IST) and are approximate. Timings vary slightly by your exact location.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Tool: Lucky Picks ─────────────────────────── */
function LuckyTool() {
  const [sign, setSign] = useState('Aries')
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const data = useMemo(() => {
    const seed = hash(`${sign}|${today}`)
    return {
      number: (seed % 9) + 1,
      colour: pick(LUCKY_COLORS, seed),
      gemstone: pick(GEMSTONES, seed >> 2),
      direction: pick(DIRECTIONS, seed >> 3),
      metal: pick(METALS, seed >> 4),
      mantra: pick(MANTRAS, seed >> 5),
      time: `${((seed >> 6) % 12) + 1}:00 – ${((seed >> 6) % 12) + 2}:00`,
    }
  }, [sign, today])

  return (
    <div className="at-tool-grid">
      <div className="at-form">
        <div>
          <span className="flbl">Your Zodiac Sign · Tap Yours</span>
          <div className="lucky-signs">
            {RASHIS.map((r) => (
              <button
                key={r.en}
                type="button"
                className={`lucky-sign${r.en === sign ? ' active' : ''}`}
                onClick={() => setSign(r.en)}
                aria-pressed={r.en === sign}
              >
                <span className="lucky-sign-glyph">{r.glyph}</span>
                <span className="lucky-sign-name">{r.en}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="at-lucky-hero">
          <span className="flbl">Lucky Number Today</span>
          <span className="at-lucky-number">{data.number}</span>
          <span className="at-lucky-mantra">{data.mantra}</span>
        </div>
      </div>

      <div className="at-result">
        <div className="at-result-head">
          <span className="at-result-glyph">🍀</span>
          <div>
            <h3>Fortune for {sign}</h3>
            <p>Your cosmic picks for today</p>
          </div>
        </div>
        <div className="at-lucky-grid">
          <div className="at-lucky-card"><span>🎨</span><p className="flbl">Colour</p><strong>{data.colour}</strong></div>
          <div className="at-lucky-card"><span>💎</span><p className="flbl">Gemstone</p><strong>{data.gemstone}</strong></div>
          <div className="at-lucky-card"><span>🧭</span><p className="flbl">Direction</p><strong>{data.direction}</strong></div>
          <div className="at-lucky-card"><span>🔗</span><p className="flbl">Metal</p><strong>{data.metal}</strong></div>
          <div className="at-lucky-card"><span>⏰</span><p className="flbl">Lucky Hour</p><strong>{data.time}</strong></div>
          <div className="at-lucky-card"><span>🔢</span><p className="flbl">Lucky No.</p><strong>{data.number}</strong></div>
        </div>
        <p className="at-note">Picks refresh every day. Wear your lucky colour and face your lucky direction while praying for best results.</p>
      </div>
    </div>
  )
}

/* ─────────────────────────── Tool: Numerology ─────────────────────────── */
function NumerologyTool({ showToast }) {
  const [dob, setDob] = useState('')
  const [result, setResult] = useState(null)

  const calc = (e) => {
    e.preventDefault()
    const date = parseDate(dob)
    if (!date) return showToast('Please enter a valid date of birth.')
    const moolank = digitRoot(date.getDate())
    const d = date.getDate()
    const m = date.getMonth() + 1
    const y = date.getFullYear()
    const bhagyank = digitRoot(digitRoot(d) + digitRoot(m) + digitRoot(y))
    setResult({
      moolank,
      bhagyank,
      moolInfo: NUMBER_PLANETS[moolank],
      bhagyaInfo: NUMBER_PLANETS[bhagyank],
      when: fmtLongDate(date),
    })
    showToast('Your birth numbers are revealed. 🔢')
  }

  return (
    <div className="at-tool-grid">
      <form className="at-form" onSubmit={calc}>
        <div>
          <label className="flbl" htmlFor="n-dob">Date of Birth *</label>
          <DateField id="n-dob" value={dob} onChange={(e) => setDob(e.target.value)} max={todayISO()} required />
        </div>
        <p className="at-form-legend">
          Your <strong>Moolank</strong> (root number) comes from your day of birth, while your <strong>Bhagyank</strong>
          (destiny number) is drawn from your full birth date.
        </p>
        <button type="submit" className="at-submit shine-btn">Reveal My Numbers ✦</button>
      </form>

      <div className="at-result">
        {result ? (
          <>
            <div className="at-num-hero">
              <div className="at-num-badge">
                <span className="at-num-big">{result.moolank}</span>
                <span className="flbl">Moolank</span>
              </div>
              <div className="at-num-badge">
                <span className="at-num-big">{result.bhagyank}</span>
                <span className="flbl">Bhagyank</span>
              </div>
            </div>
            <ResultRow label="Ruling Planet" value={result.moolInfo.planet} accent />
            <ResultRow label="Destiny Planet" value={result.bhagyaInfo.planet} accent />
            <ResultRow label="Lucky Day" value={result.moolInfo.day} />
            <ResultRow label="Lucky Colour" value={result.moolInfo.colour} />
            <div className="at-traits">
              <p className="flbl">Your Nature</p>
              <p>{result.moolInfo.traits}</p>
            </div>
            <p className="at-note">Born on {result.when}. Numerology reveals tendencies — pair it with your Kundli for a fuller picture.</p>
          </>
        ) : (
          <EmptyState icon="🔢" text="Enter your date of birth to discover your ruling numbers." />
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Wheel of Fortune ─────────────────────────── */
const WHEEL_PRIZES = [
  { icon: '🔮', label: 'Lucky Number', message: 'A fortunate number shines on you today. Keep an eye out for it!', fill: '#6D1414' },
  { icon: '💰', label: 'Wealth Yoga', message: 'The stars favour your finances. A prosperous window is opening.', fill: '#B45309' },
  { icon: '❤️', label: 'Love & Harmony', message: 'Venus smiles on your relationships. Express what is in your heart.', fill: '#7A1F2B' },
  { icon: '🕉️', label: 'Divine Blessing', message: 'Ganesha removes an obstacle from your path. Move forward with faith.', fill: '#8A4B0A' },
  { icon: '🌟', label: 'Star Favour', message: 'A lucky break is on the horizon. Say yes to a new opportunity.', fill: '#5C1A08' },
  { icon: '🏆', label: 'Success Ahead', message: 'Your effort is about to be rewarded. Victory is written for you.', fill: '#9A3412' },
  { icon: '🍀', label: 'Extra Fortune', message: 'Fortune doubles! Wear green today and spin your luck further.', fill: '#4A1A00' },
  { icon: '🌿', label: 'Health & Peace', message: 'Healing energy surrounds you. Rest, breathe, and restore balance.', fill: '#7B3008' },
]
const WHEEL_SEG = 360 / WHEEL_PRIZES.length

function polar(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }
}
function slicePath(cx, cy, r, startAngle, endAngle) {
  const s = polar(cx, cy, r, endAngle)
  const e = polar(cx, cy, r, startAngle)
  const large = endAngle - startAngle > 180 ? 1 : 0
  return `M ${cx} ${cy} L ${s.x} ${s.y} A ${r} ${r} 0 ${large} 0 ${e.x} ${e.y} Z`
}

function WheelOfFortune({ showToast }) {
  const [rotation, setRotation] = useState(0)
  const [spinning, setSpinning] = useState(false)
  const [result, setResult] = useState(null)
  const [spins, setSpins] = useState(0)
  const pendingRef = useRef(null)

  const spin = () => {
    if (spinning) return
    setSpinning(true)
    setResult(null)
    const i = Math.floor(Math.random() * WHEEL_PRIZES.length)
    pendingRef.current = i
    const turns = 5 + Math.floor(Math.random() * 3)
    // Land the centre of segment i under the top pointer (0° = top)
    const targetMod = (360 - (i * WHEEL_SEG + WHEEL_SEG / 2)) % 360
    setRotation((prev) => {
      const currentMod = ((prev % 360) + 360) % 360
      const delta = (targetMod - currentMod + 360) % 360
      return prev + turns * 360 + delta
    })
  }

  const onSpinEnd = () => {
    if (pendingRef.current == null) return
    const prize = WHEEL_PRIZES[pendingRef.current]
    pendingRef.current = null
    setSpinning(false)
    setResult(prize)
    setSpins((n) => n + 1)
    showToast(`🎉 You won ${prize.label}!`)
  }

  return (
    <div className="wof-layout">
      <div className="wof-stage">
        <div className="wof-pointer" aria-hidden="true" />
        <div
          className="wof-disc"
          style={{ transform: `rotate(${rotation}deg)` }}
          onTransitionEnd={onSpinEnd}
        >
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="wof-svg">
            <circle cx="100" cy="100" r="99" fill="#160B05" />
            {WHEEL_PRIZES.map((p, i) => {
              const start = i * WHEEL_SEG
              const end = start + WHEEL_SEG
              const mid = start + WHEEL_SEG / 2
              const pos = polar(100, 100, 66, mid)
              return (
                <g key={p.label}>
                  <path d={slicePath(100, 100, 96, start, end)} fill={p.fill} stroke="#E8A33D" strokeWidth="0.8" />
                  <g transform={`rotate(${mid} ${pos.x} ${pos.y})`}>
                    <text x={pos.x} y={pos.y + 6} textAnchor="middle" fontSize="19">{p.icon}</text>
                  </g>
                </g>
              )
            })}
            <circle cx="100" cy="100" r="96" fill="none" stroke="#E8A33D" strokeWidth="2" opacity="0.7" />
            {WHEEL_PRIZES.map((_, i) => {
              const pt = polar(100, 100, 96, i * WHEEL_SEG)
              return <circle key={i} cx={pt.x} cy={pt.y} r="2.2" fill="#F0D9A0" />
            })}
          </svg>
        </div>
        <button
          type="button"
          className="wof-hub"
          onClick={spin}
          disabled={spinning}
          aria-label="Spin the wheel"
        >
          {spinning ? '…' : 'SPIN'}
        </button>
      </div>

      <div className="wof-side">
        {result ? (
          <div className="wof-result">
            <div className="wof-result-icon">{result.icon}</div>
            <span className="wof-result-badge">You Won</span>
            <h3>{result.label}</h3>
            <p>{result.message}</p>
            <button type="button" className="wof-again" onClick={spin} disabled={spinning}>
              Spin Again ↻
            </button>
          </div>
        ) : (
          <div className="wof-intro">
            <span className="wof-result-badge">Daily Luck</span>
            <h3>Spin the Wheel of Fortune</h3>
            <p>
              Give the cosmic wheel a spin and let the universe reveal today&apos;s blessing — a lucky number, a favour
              from the planets, or a divine boon.
            </p>
            <button type="button" className="wof-again" onClick={spin} disabled={spinning}>
              {spinning ? 'Spinning…' : 'Spin Now ✦'}
            </button>
          </div>
        )}
        {spins > 0 && <p className="wof-count">Spins today · {spins}</p>}
      </div>
    </div>
  )
}

/* ─────────────────────────── Festivals & Vrat ─────────────────────────── */
// Major Hindu festivals & vrats for 2026 (Gregorian dates; may vary regionally by local panchang)
const FESTIVALS = [
  { date: '2026-01-14', name: 'Makar Sankranti', icon: '🌾', tag: 'Harvest', desc: 'The Sun enters Capricorn — kite-flying, sesame sweets and holy dips mark the turn towards longer days.' },
  { date: '2026-01-23', name: 'Vasant Panchami', icon: '📖', tag: 'Puja', desc: 'Devotees honour Goddess Saraswati, patron of learning, music and the arts, wearing yellow for spring.' },
  { date: '2026-02-15', name: 'Maha Shivratri', icon: '🔱', tag: 'Vrat', desc: 'The great night of Lord Shiva — kept with fasting, night-long vigil and offerings of bilva leaves.' },
  { date: '2026-03-03', name: 'Holika Dahan', icon: '🔥', tag: 'Festival', desc: 'Bonfires are lit on the eve of Holi to celebrate the triumph of devotion over evil.' },
  { date: '2026-03-04', name: 'Holi', icon: '🎨', tag: 'Festival', desc: 'The festival of colours welcomes spring with music, sweets and joyful abandon.' },
  { date: '2026-03-19', name: 'Ugadi / Gudi Padwa', icon: '🌸', tag: 'New Year', desc: 'The lunar new year of the Deccan — new beginnings, neem-jaggery and festive rangoli.' },
  { date: '2026-03-26', name: 'Ram Navami', icon: '🏹', tag: 'Puja', desc: 'The birth of Lord Rama, observed with recitations of the Ramayana and temple processions.' },
  { date: '2026-04-20', name: 'Akshaya Tritiya', icon: '💰', tag: 'Auspicious', desc: 'An ever-auspicious day for new ventures, gold purchases and acts of charity.' },
  { date: '2026-05-01', name: 'Buddha Purnima', icon: '🪷', tag: 'Purnima', desc: 'The birth, enlightenment and nirvana of Gautama Buddha, observed on the full moon of Vaisakha.' },
  { date: '2026-06-16', name: 'Jagannath Rath Yatra', icon: '🛕', tag: 'Yatra', desc: 'The grand chariot festival of Puri, when Lord Jagannath journeys among his devotees.' },
  { date: '2026-07-29', name: 'Guru Purnima', icon: '🙏', tag: 'Purnima', desc: 'A day to honour one’s guru and spiritual teachers on the full moon of Ashadha.' },
  { date: '2026-08-08', name: 'Nag Panchami', icon: '🐍', tag: 'Puja', desc: 'Serpent deities are worshipped with milk and prayers for protection and prosperity.' },
  { date: '2026-08-28', name: 'Raksha Bandhan', icon: '🧵', tag: 'Festival', desc: 'Sisters tie the sacred rakhi on their brothers’ wrists, a bond of love and protection.' },
  { date: '2026-09-04', name: 'Krishna Janmashtami', icon: '🦚', tag: 'Vrat', desc: 'The midnight birth of Lord Krishna, kept with fasting, bhajans and dahi-handi revelry.' },
  { date: '2026-09-14', name: 'Ganesh Chaturthi', icon: '🐘', tag: 'Festival', desc: 'Ten days of devotion to Lord Ganesha, the remover of obstacles, ending in joyful visarjan.' },
  { date: '2026-10-11', name: 'Sharad Navratri Begins', icon: '🪔', tag: 'Navratri', desc: 'Nine nights honouring Goddess Durga in her nine forms, with fasting, garba and worship.' },
  { date: '2026-10-20', name: 'Dussehra', icon: '🏹', tag: 'Festival', desc: 'Vijayadashami marks Rama’s victory over Ravana — effigies burn as good conquers evil.' },
  { date: '2026-11-08', name: 'Diwali', icon: '🪔', tag: 'Festival', desc: 'The festival of lights — Lakshmi puja, diyas and fireworks welcome prosperity and joy.' },
  { date: '2026-11-24', name: 'Guru Nanak Jayanti', icon: '☬', tag: 'Purnima', desc: 'The birth of Guru Nanak Dev Ji, observed with prabhat pheris, kirtan and langar.' },
]

const DAY_MS = 86400000

function FestivalsSection() {
  const [filter, setFilter] = useState('upcoming')

  const { items, next } = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const mapped = FESTIVALS.map((f) => {
      const d = new Date(`${f.date}T00:00:00`)
      const days = Math.round((d.getTime() - today.getTime()) / DAY_MS)
      return { ...f, d, days, upcoming: days >= 0 }
    })
    return { items: mapped, next: mapped.find((f) => f.upcoming) || null }
  }, [])

  const list = filter === 'upcoming' ? items.filter((f) => f.upcoming) : items
  const fmt = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' })
  const countdown = (days) => (days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days} days`)

  return (
    <div>
      {next && (
        <Reveal className="fest-next">
          <div className="fest-next-icon">{next.icon}</div>
          <div className="fest-next-body">
            <span className="fest-next-badge">Next Festival · {countdown(next.days)}</span>
            <h3>{next.name}</h3>
            <p className="fest-next-date">{fmt(next.d)} 2026</p>
            <p className="fest-next-desc">{next.desc}</p>
          </div>
        </Reveal>
      )}

      <div className="fest-tabs">
        <button type="button" className={`at-tab${filter === 'upcoming' ? ' active' : ''}`} onClick={() => setFilter('upcoming')}>
          Upcoming
        </button>
        <button type="button" className={`at-tab${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>
          Whole Year
        </button>
      </div>

      {list.length === 0 ? (
        <p className="fest-empty">No more festivals left in 2026 — check back in the new year. 🙏</p>
      ) : (
        <div className="fest-grid">
          {list.map((f) => (
            <Reveal key={f.name} className={`fest-card${f.upcoming ? '' : ' fest-past'}`}>
              <div className="fest-card-top">
                <span className="fest-card-icon">{f.icon}</span>
                <span className="fest-card-tag">{f.tag}</span>
              </div>
              <h4>{f.name}</h4>
              <p className="fest-card-date">{fmt(f.d)}</p>
              <p className="fest-card-desc">{f.desc}</p>
              {f.upcoming ? (
                <span className="fest-card-count">{countdown(f.days)}</span>
              ) : (
                <span className="fest-card-count fest-card-past">Passed</span>
              )}
            </Reveal>
          ))}
        </div>
      )}

      <p className="at-note" style={{ textAlign: 'center', marginTop: '24px' }}>
        Dates are for 2026 and may vary slightly by region and local panchang. Confirm with your family priest for vrat and muhurat timings.
      </p>
    </div>
  )
}

/* ─────────────────────────── Daily Horoscope widget ─────────────────────────── */
const HORO_SIGNS = [
  { name: 'Aries', glyph: '♈' }, { name: 'Taurus', glyph: '♉' },
  { name: 'Gemini', glyph: '♊' }, { name: 'Cancer', glyph: '♋' },
  { name: 'Leo', glyph: '♌' }, { name: 'Virgo', glyph: '♍' },
  { name: 'Libra', glyph: '♎' }, { name: 'Scorpio', glyph: '♏' },
  { name: 'Sagittarius', glyph: '♐' }, { name: 'Capricorn', glyph: '♑' },
  { name: 'Aquarius', glyph: '♒' }, { name: 'Pisces', glyph: '♓' },
]
// Transits are absolute per date — fetch once, reuse across every sign switch.
const transitCache = {}

function HoroscopeWidget() {
  const [sign, setSign] = useState('Aries')
  const [view, setView] = useState('load') // 'load' | 'error' | 'content'
  const [result, setResult] = useState({ text: '', luckyNum: '', luckyColor: '', luckyMood: '' })
  const reqRef = useRef(0)

  const load = () => {
    const my = ++reqRef.current
    setView('load')
    const dateStr = new Date().toISOString().slice(0, 10)
    const transitsPromise = transitCache[dateStr] || (transitCache[dateStr] = getPlanetTransits(dateStr))
    transitsPromise
      .then((transits) => {
        if (my !== reqRef.current) return
        const { text, luckyNum, luckyColor, luckyMood } = buildTransitReading(sign, 'today', transits)
        setResult({ text, luckyNum, luckyColor, luckyMood })
        setView('content')
      })
      .catch(() => {
        delete transitCache[dateStr]
        if (my === reqRef.current) setView('error')
      })
  }

  useEffect(load, [sign]) // eslint-disable-line react-hooks/exhaustive-deps

  const active = HORO_SIGNS.find((s) => s.name === sign)
  const todayLabel = useMemo(
    () => new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
    [],
  )

  return (
    <div className="horo-layout">
      <div className="horo-signs">
        {HORO_SIGNS.map((s) => (
          <button
            key={s.name}
            type="button"
            className={`horo-chip${s.name === sign ? ' active' : ''}`}
            onClick={() => setSign(s.name)}
          >
            <span className="horo-chip-glyph">{s.glyph}</span>
            <span className="horo-chip-name">{s.name}</span>
          </button>
        ))}
      </div>

      <div className="horo-card">
        <div className="horo-card-head">
          <span className="horo-card-glyph">{active.glyph}</span>
          <div>
            <h3>{active.name}</h3>
            <p>Today · {todayLabel}</p>
          </div>
        </div>

        {view === 'load' && <HoroscopeWidgetSkeleton />}

        {view === 'error' && (
          <div className="horo-error">
            <div className="horo-error-icon">🔭</div>
            <p>We couldn&apos;t reach the horoscope service right now.</p>
            <button type="button" className="wof-again" onClick={load}>Retry</button>
          </div>
        )}

        {view === 'content' && (
          <div>
            <p className="horo-text">{result.text}</p>
            <div className="horo-lucky">
              <div><span className="flbl">Lucky No.</span><strong>{result.luckyNum}</strong></div>
              <div><span className="flbl">Colour</span><strong>{result.luckyColor}</strong></div>
              <div><span className="flbl">Mood</span><strong>{result.luckyMood}</strong></div>
            </div>
            <Link to="/horoscope" className="horo-more">
              Weekly &amp; monthly forecasts
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─────────────────────────── Ask the Oracle ─────────────────────────── */
const ORACLE_CARDS = [
  { icon: '☀️', name: 'The Sun', verdict: 'Yes', keyword: 'Vitality', message: 'Radiant success shines upon you. Step into the light with confidence — this is your time to flourish.' },
  { icon: '🌙', name: 'The Moon', verdict: 'Perhaps', keyword: 'Intuition', message: 'Not all is yet revealed. Trust your inner voice and wait for the shadows to clear before you decide.' },
  { icon: '⭐', name: 'The Star', verdict: 'Yes', keyword: 'Hope', message: 'Hope is restored and the universe conspires in your favour. Keep faith — your wish draws near.' },
  { icon: '🪷', name: 'Sacred Lotus', verdict: 'Yes', keyword: 'Growth', message: 'Even from muddy waters, beauty blooms. Your struggles are ripening into spiritual growth.' },
  { icon: '🕉️', name: 'Om', verdict: 'Yes', keyword: 'Alignment', message: 'You are divinely aligned. Proceed in peace — the cosmos supports the path before you.' },
  { icon: '🪔', name: 'The Diya', verdict: 'Yes', keyword: 'Clarity', message: 'A steady flame lights your way. Doubt dissolves and the right choice becomes clear.' },
  { icon: '🦚', name: 'The Peacock', verdict: 'Perhaps', keyword: 'Renewal', message: 'Beauty and renewal are near, yet pride must soften first. Lead with humility.' },
  { icon: '🐚', name: 'The Conch', verdict: 'Yes', keyword: 'Beginnings', message: 'A sacred beginning is announced. Answer the call — a new chapter opens for you.' },
  { icon: '🔱', name: 'The Trishul', verdict: 'Yes', keyword: 'Strength', message: 'Your inner strength conquers the obstacle before you. Stand firm and act with resolve.' },
  { icon: '🐍', name: 'The Serpent', verdict: 'Perhaps', keyword: 'Transformation', message: 'Transformation asks you to shed the old skin first. Release what no longer serves you.' },
  { icon: '⛰️', name: 'The Mountain', verdict: 'No', keyword: 'Patience', message: 'Not yet. The time is not ripe — be patient, and prepare while the season turns.' },
  { icon: '🌊', name: 'The River', verdict: 'Perhaps', keyword: 'Surrender', message: 'Let go and flow. Forcing the current will only exhaust you; ease brings you home.' },
  { icon: '🔥', name: 'The Flame', verdict: 'Yes', keyword: 'Passion', message: 'Passion fuels you now. Act boldly while the fire burns bright within your heart.' },
  { icon: '⛈️', name: 'The Storm', verdict: 'No', keyword: 'Caution', message: 'Hold. Turbulence surrounds this choice — wait for calmer skies before you move.' },
  { icon: '🐘', name: 'The Elephant', verdict: 'Yes', keyword: 'Obstacles Cleared', message: 'The remover of obstacles walks with you. The way that was blocked now opens wide.' },
  { icon: '☸️', name: 'The Wheel', verdict: 'Perhaps', keyword: 'Destiny', message: 'Fortune turns as it must. What is low shall rise again — nothing stays still for long.' },
]

function OracleSection() {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState(null)
  const [asking, setAsking] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  const ask = (e) => {
    e.preventDefault()
    const q = question.trim()
    if (!q) return
    // The same question always draws the same guidance — the oracle is consistent.
    const card = pick(ORACLE_CARDS, hash(q))
    setAsking(true)
    setAnswer(null)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setAnswer({ ...card, q })
      setAsking(false)
    }, 900)
  }

  return (
    <form className="oracle-wrap" onSubmit={ask}>
      <textarea
        className="field oracle-ask"
        rows="3"
        value={question}
        onChange={(e) => {
          setQuestion(e.target.value)
          if (answer) setAnswer(null)
        }}
        placeholder="Type your question for the oracle… e.g. “Will my new venture succeed?”"
        aria-label="Your question for the oracle"
        required
      />
      <button type="submit" className="wof-again oracle-submit" disabled={asking}>
        {asking ? 'Consulting the stars…' : 'Ask the Oracle ✦'}
      </button>

      {asking && (
        <div className="oracle-thinking">
          <span className="horo-spinner" />
          <p>The oracle is listening…</p>
        </div>
      )}

      {answer && !asking && (
        <div className="oracle-result">
          <p className="oracle-q">“{answer.q}”</p>
          <span className={`oracle-verdict oracle-${answer.verdict.toLowerCase()}`}>{answer.verdict}</span>
          <h3>
            {answer.icon} {answer.name}
          </h3>
          <p className="oracle-msg">{answer.message}</p>
          <span className="oracle-keyword">Guidance · {answer.keyword}</span>
        </div>
      )}
    </form>
  )
}

/* ─────────────────────────── Top section-nav menu ─────────────────────────── */
// The 5 tools live inside the tabbed workbench; the 4 features are standalone sections.
const TOOL_NAV = [
  { key: 'kundli', label: 'Free Kundli', icon: '📜' },
  { key: 'matching', label: 'Kundli Matching', icon: '💞' },
  { key: 'panchang', label: 'Panchang', icon: '📅' },
  { key: 'lucky', label: 'Daily Lucky Picks', icon: '🍀' },
  { key: 'numerology', label: 'Birth Number', icon: '🔢' },
]
const FEATURE_NAV = [
  { id: 'studio', label: 'Kundli Studio', icon: '🪐' },
  { id: 'wheel', label: 'Wheel', icon: '🎡' },
  { id: 'festivals', label: 'Festivals', icon: '🪔' },
  { id: 'horoscope', label: 'Horoscope', icon: '🔮' },
]
const NAV_OFFSET = 140 // fixed header + sticky nav height

function SectionNav({ activeTool, onSelectTool }) {
  const [inView, setInView] = useState('at-workbench')

  useEffect(() => {
    const ids = ['at-workbench', ...FEATURE_NAV.map((f) => f.id)]
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setInView(entry.target.id)
        })
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  const scrollTo = (id) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  const goTool = (key) => (e) => {
    e.preventDefault()
    onSelectTool(key)
    scrollTo('at-workbench')
  }
  const goFeature = (id) => (e) => {
    e.preventDefault()
    scrollTo(id)
  }

  return (
    <nav className="section-nav" aria-label="Section navigation">
      <div className="section-nav-inner">
        {TOOL_NAV.map((t) => {
          const on = inView === 'at-workbench' && activeTool === t.key
          return (
            <a
              key={t.key}
              href="#at-workbench"
              onClick={goTool(t.key)}
              className={`section-tab${on ? ' active' : ''}`}
              aria-current={on ? 'true' : undefined}
            >
              <span className="section-tab-ico">{t.icon}</span>
              {t.label}
            </a>
          )
        })}
        <span className="section-nav-divider" aria-hidden="true" />
        {FEATURE_NAV.map((f) => (
          <a
            key={f.id}
            href={`#${f.id}`}
            onClick={goFeature(f.id)}
            className={`section-tab${inView === f.id ? ' active' : ''}`}
            aria-current={inView === f.id ? 'true' : undefined}
          >
            <span className="section-tab-ico">{f.icon}</span>
            {f.label}
          </a>
        ))}
      </div>
    </nav>
  )
}

/* ─────────────────────────── Page ─────────────────────────── */
const TOOL_COMPONENTS = {
  kundli: KundliTool,
  matching: MatchingTool,
  panchang: PanchangTool,
  lucky: LuckyTool,
  numerology: NumerologyTool,
}

export default function AstroToolsPage() {
  const { showToast } = useToast()
  const [active, setActive] = useState('kundli')
  const ActiveTool = TOOL_COMPONENTS[active]
  const activeMeta = TOOLS.find((t) => t.key === active)

  const selectTool = (key) => {
    setActive(key)
    const el = document.getElementById('at-workbench')
    if (el) window.scrollTo({ top: el.offsetTop - 110, behavior: 'smooth' })
  }

  return (
    <PageLayout className="astro-tools-page text-astroDark antialiased overflow-x-hidden">

      {/* ── HERO / BREADCRUMB BANNER ── */}
      <header className="relative pt-30 sm:pt-32.5 lg:pt-37.5 pb-20 lg:pb-28 overflow-hidden bg-[#5A0808] text-white text-center border-b border-cosmicRed">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] bg-cosmicRed/30 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square opacity-[0.07] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="50" strokeWidth="0.5" />
            <path d="M100 2v196M2 100h196" strokeWidth="0.3" />
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-goldPrimary/50" />
            <span className="text-[9px] uppercase font-bold tracking-[0.4em] text-goldPrimary/80 font-royal">ॐ Free Vedic Tools</span>
            <span className="w-8 h-px bg-goldPrimary/50" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-royal text-[#FBF3E4] leading-tight">
            Astro{' '}
            <span className="bg-linear-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent">
              Tools
            </span>
          </h1>
          <p className="text-sm sm:text-base text-white/60 font-light mt-5 max-w-xl mx-auto leading-relaxed">
            Generate your Kundli, check marriage compatibility, read today&apos;s Panchang, find your lucky picks and
            birth number — plus a full Kundli Studio with divisional charts and planetary calculations. All free,
            in one place, no sign-up needed.
          </p>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-7 px-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/50">
            <Link to="/" className="hover:text-goldPrimary transition-colors">Home</Link>
            <span className="text-goldPrimary/50">/</span>
            <span className="text-goldPrimary">Astro Tools</span>
          </nav>
        </div>
      </header>

      {/* ── STICKY SECTION NAV (top menu) ── */}
      <SectionNav activeTool={active} onSelectTool={setActive} />

      {/* ── TOOL PICKER GRID ── */}
      <section id="tools" className="relative py-16 lg:py-20 md:px-10" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-amber-700/70">Choose a Tool</span>
            <h2 className="text-3xl lg:text-[40px] text-astroDark mt-3">Five Free Astrology Tools</h2>
            <p className="text-sm text-stone-500 font-light mt-4 leading-relaxed">
              Tap any tool below to get instant Vedic insights. Everything runs right here in your browser.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {TOOLS.map((t, i) => (
              <Reveal
                key={t.key}
                as="button"
                type="button"
                onClick={() => selectTool(t.key)}
                className={`at-tool-card${active === t.key ? ' active' : ''}`}
                style={{ transitionDelay: `${i * 0.05}s` }}
              >
                <div className="at-tool-icon">{t.icon}</div>
                <span className="at-tool-badge">{t.tag}</span>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACTIVE TOOL WORKBENCH ── */}
      <section id="at-workbench" className="relative py-16 lg:py-24 overflow-hidden" style={{ background: 'linear-gradient(180deg,#FFFFFF 0%,#FDFAF5 50%,#FFFFFF 100%)' }}>
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute -top-32 -left-32 w-[55vw] max-w-160 h-[55vw] max-h-160 rounded-full" style={{ background: 'radial-gradient(circle,rgba(245,158,11,.10) 0%,transparent 70%)', filter: 'blur(80px)' }} />
          <div className="absolute -bottom-32 -right-32 w-[45vw] max-w-135 h-[45vw] max-h-135 rounded-full" style={{ background: 'radial-gradient(circle,rgba(217,119,6,.08) 0%,transparent 70%)', filter: 'blur(90px)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Tab switcher */}
          <div className="at-tabs">
            {TOOLS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`at-tab${active === t.key ? ' active' : ''}`}
                onClick={() => setActive(t.key)}
              >
                <span className="at-tab-ico">{t.icon}</span>
                {t.title}
              </button>
            ))}
          </div>

          <div className="at-panel">
            <div className="at-panel-head">
              <span className="at-panel-icon">{activeMeta.icon}</span>
              <div>
                <h2>{activeMeta.title}</h2>
                <p>{activeMeta.desc}</p>
              </div>
            </div>
            <ActiveTool showToast={showToast} />
          </div>
        </div>
      </section>

      {/* ── KUNDLI STUDIO (advanced charts & calculations) ── */}
      <section id="studio" className="relative py-16 lg:py-24 md:px-10" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-amber-700/70">Advanced Jyotish Lab</span>
            <h2 className="text-3xl lg:text-[40px] text-astroDark mt-3">Kundli Studio</h2>
            <p className="text-sm text-stone-500 font-light mt-4 leading-relaxed">
              Go beyond the basics — cast all 16 divisional charts in the classic North Indian style and study every
              planet, house and classical detail of your chart, computed live by our Vedic engine.
            </p>
          </Reveal>
          <Reveal>
            <KundliStudio />
          </Reveal>
        </div>
      </section>

      {/* ── WHEEL OF FORTUNE ── */}
      <section id="wheel" className="relative py-16 lg:py-24 overflow-hidden" style={{ background: 'radial-gradient(ellipse 90% 80% at 50% 0%,#2A1206 0%,#160B05 55%,#0B0806 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[60vw] max-w-140 h-[60vw] max-h-140 rounded-full bg-goldGlow/10 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 relative z-10">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-goldPrimary/70">Try Your Luck</span>
            <h2 className="text-3xl lg:text-[40px] text-white mt-3">Wheel of Fortune</h2>
            <p className="text-sm text-white/50 font-light mt-4 leading-relaxed">
              Give the sacred wheel a spin and receive a cosmic blessing chosen just for you.
            </p>
          </Reveal>
          <Reveal>
            <WheelOfFortune showToast={showToast} />
          </Reveal>
        </div>
      </section>

      {/* ── FESTIVALS & VRAT ── */}
      <section id="festivals" className="relative py-16 lg:py-24 md:px-10" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-amber-700/70">Sacred Calendar</span>
            <h2 className="text-3xl lg:text-[40px] text-astroDark mt-3">Festivals &amp; Vrat 2026</h2>
            <p className="text-sm text-stone-500 font-light mt-4 leading-relaxed">
              Plan your year of devotion — see when each festival, vrat and auspicious day falls, and how many days away it is.
            </p>
          </Reveal>
          <FestivalsSection />
        </div>
      </section>

      {/* ── DAILY HOROSCOPE ── */}
      <section id="horoscope" className="relative py-16 lg:py-24 overflow-hidden" style={{ background: 'linear-gradient(180deg,#0B0806 0%,#140C07 50%,#0B0806 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/4 right-[-10%] w-[45vw] h-[45vw] rounded-full bg-goldGlow/10 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 relative z-10">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-goldPrimary/70">Cosmic Forecast</span>
            <h2 className="text-3xl lg:text-[40px] text-white mt-3">Today&apos;s Horoscope</h2>
            <p className="text-sm text-white/50 font-light mt-4 leading-relaxed">
              Pick your zodiac sign and read what the planets have aligned for you today — updated live every day.
            </p>
          </Reveal>
          <Reveal>
            <HoroscopeWidget />
          </Reveal>
        </div>
      </section>

      {/* ── ASK THE ORACLE ── */}
      <section id="oracle" className="relative py-16 lg:py-24 overflow-hidden" style={{ background: 'radial-gradient(ellipse 90% 80% at 50% 0%,#2A1A3A 0%,#191029 50%,#0B0806 100%)' }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[55vw] max-w-130 h-[55vw] max-h-130 rounded-full bg-goldGlow/10 blur-3xl" />
          <div className="absolute top-10 left-[12%] text-goldPrimary/20 text-xl select-none">✦</div>
          <div className="absolute bottom-16 right-[14%] text-goldPrimary/20 text-2xl select-none">✧</div>
          <div className="absolute top-1/2 right-[8%] text-goldPrimary/15 text-lg select-none">✦</div>
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-royal text-[9px] uppercase font-bold tracking-[.45em] text-goldPrimary/70">Divine Guidance</span>
            <h2 className="text-3xl lg:text-[40px] text-white mt-3">Ask the Oracle</h2>
            <p className="text-sm text-white/50 font-light mt-4 leading-relaxed">
              Still your mind, hold your question, and let the sacred cards reveal the wisdom the universe has for you today.
            </p>
          </Reveal>
          <Reveal>
            <OracleSection />
          </Reveal>
        </div>
      </section>

      {/* ── WHY / DISCLAIMER STRIP ── */}
      <section className="relative py-16 lg:py-20 md:px-10" style={{ background: '#FDFAF5' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: '🎁', title: '100% Free', body: 'Every tool is free to use, with no hidden charges and no account required.' },
              { icon: '⚡', title: 'Instant Results', body: 'Answers are generated instantly in your browser — nothing is stored or shared.' },
              { icon: '🧘', title: 'Rooted in Vedic Tradition', body: 'Built on classical Jyotish principles for guidance you can trust.' },
            ].map((f) => (
              <Reveal key={f.title} className="val-card p-7 text-center">
                <div className="w-12 h-12 mx-auto rounded-2xl bg-amber-50 flex items-center justify-center text-2xl mb-4">{f.icon}</div>
                <h3 className="text-base text-astroDark">{f.title}</h3>
                <p className="text-[13px] text-stone-500 font-light mt-2 leading-relaxed">{f.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section className="relative py-16 lg:py-20 overflow-hidden" style={{ background: 'radial-gradient(ellipse 120% 100% at 50% 0%,#C2710C 0%,#92400E 30%,#4A1A05 65%,#1A0803 100%)' }}>
        <div className="absolute inset-0 pointer-events-none opacity-10" aria-hidden="true">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[200px] font-royal text-white select-none">ॐ</div>
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl lg:text-[42px] text-white leading-tight">Want Guidance Made for You?</h2>
          <p className="text-sm text-white/70 font-light mt-4 max-w-xl mx-auto">
            These free tools are a starting point. For a detailed reading of your own birth chart, book a personal
            consultation with Maharaj Ji.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <a
              href={appUrl('jyotish', 'consultations')}
              className="shine-btn bg-white text-astroDark font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full shadow-xl transition-transform hover:-translate-y-0.5">
              Book Consultation
            </a>
            <Link to="/horoscope" className="border border-white/40 hover:border-white text-white font-semibold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all hover:bg-white/10">
              View Horoscope
            </Link>
          </div>
        </div>
      </section>

    </PageLayout>
  )
}
